/**
 * Carries a file dropped on the Studio front door to the import page.
 *
 * ─── The defect this closes ──────────────────────────────────────────────
 * `hero-chat-input.tsx` used to handle a drop like this:
 *
 *     if (e.dataTransfer.files.length > 0) onSourceSelect?.()
 *
 * The file was read only to decide whether to navigate, then discarded.
 * Measured 2026-07-30: dropping a `.docx` on `/studio` moved the creator to
 * `/studio/import` with no mention of the file, no error, and no network
 * request. The product swallowed the document in silence.
 *
 * ─── Why a module-scoped File and not storage ────────────────────────────
 * A `File` is not serializable to `localStorage`/`sessionStorage`, and copying
 * its bytes just to move between two routes of the SAME single-page app would
 * duplicate a multi-megabyte source for nothing: `pushStudioRoute` is a
 * `history.pushState`, so this module's state is still alive on the other side.
 * If the creator hard-navigates instead, the staged file is gone — and the
 * import page then simply shows its ordinary empty picker, which is the honest
 * outcome rather than a phantom "file attached" chip with no bytes behind it.
 *
 * ─── peek/clear rather than a destructive read ───────────────────────────
 * React StrictMode double-invokes both state initializers and effects in
 * development. A single `consume()` would therefore hand the file to one
 * invocation and `null` to the other, non-deterministically. So the read is
 * non-destructive and the consumer clears explicitly once it has applied it.
 */

import {
  DOCUMENT_SOURCE_EXTENSIONS,
  documentFormatForFileName,
  fileExtensionOf,
} from "./document-source-model";

/** Which import card can actually take this file. */
export type PendingSourceDropKind = "document" | "figma";

export interface PendingSourceDrop {
  kind: PendingSourceDropKind;
  file: File;
  /** How many files were in the drop — the extras are NOT silently taken. */
  droppedCount: number;
}

/** Extensions the `.fig` card accepts (mirrors its own `accept` attribute). */
const FIGMA_EXTENSIONS = [".fig", ".pen"] as const;

export type DroppedFileVerdict =
  | { ok: true; kind: PendingSourceDropKind }
  | { ok: false; extension: string };

/**
 * Decide, from the file NAME alone, which entrance can take this drop.
 *
 * Name-only on purpose: the real gates run afterwards on the import page —
 * `checkOpenPencilSourceFile` for `.fig`, and the extractor itself for a
 * document (a renamed `.pptx` is refused by its `[Content_Types].xml`, not by
 * its extension). This function's job is only to decide between "hand it to an
 * entrance" and "say we cannot take it", never to certify the contents.
 */
export function classifyDroppedSourceFile(fileName: string): DroppedFileVerdict {
  if (documentFormatForFileName(fileName)) return { ok: true, kind: "document" };
  const lower = fileName.trim().toLowerCase();
  if (FIGMA_EXTENSIONS.some((extension) => lower.endsWith(extension))) {
    return { ok: true, kind: "figma" };
  }
  return { ok: false, extension: fileExtensionOf(fileName) };
}

/** Every extension the front door can currently forward, for the refusal copy. */
export const DROPPABLE_EXTENSIONS: readonly string[] = [
  ...DOCUMENT_SOURCE_EXTENSIONS,
  ...FIGMA_EXTENSIONS,
];

let pendingDrop: PendingSourceDrop | null = null;

export function stagePendingSourceDrop(drop: PendingSourceDrop): void {
  pendingDrop = drop;
}

/** Non-destructive read (see the StrictMode note in the module header). */
export function readPendingSourceDrop(): PendingSourceDrop | null {
  return pendingDrop;
}

export function clearPendingSourceDrop(): void {
  pendingDrop = null;
}
